/**
 * Bilingual content resolver + runtime translation engine.
 *
 * For data that already has {english:"...", hindi:"..."} objects → instant, no API.
 * For plain-English data (all current chapter notes) → translates via Google Translate
 * with a CORRECTLY concurrent queue + localStorage cache (instant on repeat visits).
 *
 * Critical fix vs. old translation.js:
 *   processQueue is now a plain (non-async) function that starts tasks with .then()
 *   instead of awaiting inside the while-loop. This lets the loop spin up to
 *   MAX_CONCURRENT tasks simultaneously rather than processing one at a time.
 */

import {
  VERIFIED_EXACT_OVERRIDES,
  VERIFIED_RULE_OVERRIDES,
  VERIFIED_FIX_REPLACEMENTS,
} from '../config/verifiedHindiGlossary.js';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';

// ─── Language preference ───────────────────────────────────────────────────
// Source of truth: users/{uid}.preferredLanguage in Firestore (synced via
// AuthContext's live user doc listener). localStorage is only the instant
// per-browser cache so content renders before Firestore responds.

const LANG_KEY = 'ppxContentLanguage';

export const LANGUAGE_OPTIONS = [
  { id: 'english', label: 'English' },
  { id: 'hindi',   label: 'हिन्दी' },
];

export function getPreferredLanguage() {
  if (typeof window === 'undefined') return 'english';
  const v = window.localStorage.getItem(LANG_KEY);
  return LANGUAGE_OPTIONS.some(o => o.id === v) ? v : 'english';
}

// Local-only apply: cache + notify open viewers. Never writes to Firestore.
function applyLocalLanguage(lang) {
  if (typeof window === 'undefined') return;
  if (!LANGUAGE_OPTIONS.some(o => o.id === lang)) return;
  if (window.localStorage.getItem(LANG_KEY) === lang) return;
  window.localStorage.setItem(LANG_KEY, lang);
  try { window.dispatchEvent(new CustomEvent('ppx-lang', { detail: lang })); } catch (_) {}
}

export function setPreferredLanguage(lang, uid) {
  if (typeof window === 'undefined') return;
  if (!LANGUAGE_OPTIONS.some(o => o.id === lang)) return;
  applyLocalLanguage(lang);
  if (uid) {
    setDoc(doc(db, 'users', uid), { preferredLanguage: lang }, { merge: true })
      .catch(() => { /* offline — stays local until next sync */ });
  }
}

// Pull the account-level preference down into this browser's cache.
// Called from AuthContext whenever the live user doc arrives/changes.
export function syncPreferredLanguageFromCloud(userData) {
  const lang = userData?.preferredLanguage;
  if (lang && LANGUAGE_OPTIONS.some(o => o.id === lang)) applyLocalLanguage(lang);
}

// ─── Bilingual object resolver (for {english, hindi} data) ────────────────

const NON_TEXT_KEYS = new Set([
  'id',
  'slug',
  'subject',
  'type',
  'file',
  'thumb',
  'src',
  'href',
  'path',
  'svg',
  'latex',
  'equation',
]);

export function resolveText(content, language = 'english', parentKey = '') {
  if (content == null) return content;
  if (Array.isArray(content)) return content.map(i => resolveText(i, language, parentKey));
  if (typeof content === 'object' && ('english' in content || 'hindi' in content)) {
    const englishValue = content.english;
    const hindiValue = content.hindi;

    // Prefer Hindi, but if it is clearly truncated compared to English,
    // fall back to English so users never lose content in Hindi mode.
    const preferEnglishForParity = (
      language === 'hindi' &&
      typeof englishValue === 'string' &&
      typeof hindiValue === 'string' &&
      isLikelyTruncatedHindi(englishValue, hindiValue)
    );

    // Use || instead of ?? so empty strings also fall back to English
    const v = preferEnglishForParity
      ? englishValue
      : (content[language] || englishValue || hindiValue || Object.values(content)[0]);
    return resolveText(v, language, parentKey);
  }
  if (typeof content === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(content)) out[k] = resolveText(v, language, k);
    return out;
  }
  if (typeof content === 'string' && language === 'hindi' && !NON_TEXT_KEYS.has(parentKey)) {
    return applyVerifiedHindi(content);
  }
  return content;
}

function compactLength(value) {
  return String(value || '').replace(/\s+/g, '').length;
}

function isLikelyTruncatedHindi(englishText, hindiText) {
  const enLen = compactLength(englishText);
  const hiLen = compactLength(hindiText);
  if (enLen < 40) return false;
  return hiLen / enLen < 0.55;
}

// ─── Math + abbreviation extraction (protect from translation) ────────────

// All-caps abbreviations used in Indian education that must NOT be translated:
// AP, GP, HP, AGP, AM, GM, HM, JEE, NEET, IIT, NIT, LHS, RHS, etc.
const ABBREV_RE = /\b([A-Z]{2,6})\b/g;

// Named mathematicians / scientists / proper terms that appear verbatim
const NAMED_TERMS = [
  'Bohr', 'Newton', 'Einstein', 'Euler', 'Gauss', 'Pascal', 'Fibonacci',
  'Rutherford', 'Coulomb', 'Faraday', 'Avogadro', 'Rydberg', 'Planck',
  'Heisenberg', 'Schrodinger', 'Maxwell', 'Kirchhoff', 'Ohm', 'Ampere',
  'Archimedes', 'Pythagoras',
  'JEE Main', 'JEE Advanced', 'NEET UG',
];
const NAMED_RE = new RegExp(`\\b(${NAMED_TERMS.map(t => t.replace(/\s+/g, '\\s+')).join('|')})\\b`, 'g');

// Protect formula-like tokens that often appear in handbook HTML without $...$ wrappers.
// Examples: IE_1, Z_eff, SIE_1(As), 2p^3, \text{Be}, \frac{a}{b}
const SCI_TOKEN_RE = /\b(?:[A-Za-z]{1,6}(?:_[A-Za-z0-9]+)+(?:\([^)]*\))?|[0-9]+[spdfg](?:\^[0-9]+)?|[A-Za-z]{1,3}\^[0-9]+)\b/g;
const LATEX_CMD_RE = /\\[a-zA-Z]+(?:\{[^{}]*\})*/g;
const COMPARISON_EXPR_RE = /\b[A-Za-z][A-Za-z0-9_()]*\s*[<>≤≥=]+\s*[A-Za-z0-9_()]+\b/g;

// Keep these educational labels in English for natural classroom Hinglish style.
const LOCKED_ENGLISH_TERMS = [
  'Theorem', 'Proof', 'Definition', 'Example', 'Rule', 'Property', 'Identity',
  'Concept', 'Formula', 'Equation', 'Method', 'Result', 'Case', 'Corollary',
  'Question', 'Solution', 'Answer', 'Exercise', 'Practice', 'Summary',
  'Shortcut', 'Tip', 'Trick', 'Trap', 'Key Formula', 'Chapter', 'Section', 'Topic',
  'JEE Main', 'JEE Advanced', 'NEET', 'PYQ', 'AOD', 'MCQ',
  'Class 11', 'Class 12',
  'Physics', 'Chemistry', 'Biology', 'Mathematics', 'Math', 'Calculus', 'Algebra', 'Geometry',
];
const LOCKED_ENGLISH_RE = new RegExp(
  `\\b(${LOCKED_ENGLISH_TERMS.map(t => t.replace(/\s+/g, '\\s+')).join('|')})\\b`,
  'gi'
);

function extractProtected(text) {
  const map = {};
  let n = 0;
  const token = () => `⟪${n++}⟫`;
  let result = text
    // Math blocks first (most important)
    .replace(/\$\$[\s\S]+?\$\$/g,     m => { const k = token(); map[k] = m; return k; })
    .replace(/\$[^$\n]+\$/g,          m => { const k = token(); map[k] = m; return k; })
    .replace(/\\\([\s\S]+?\\\)/g,     m => { const k = token(); map[k] = m; return k; })
    .replace(/\\\[[\s\S]+?\\\]/g,     m => { const k = token(); map[k] = m; return k; })
    // Raw LaTeX/science tokens commonly present in handbook tables
    .replace(LATEX_CMD_RE,             m => { const k = token(); map[k] = m; return k; })
    .replace(COMPARISON_EXPR_RE,       m => { const k = token(); map[k] = m; return k; })
    .replace(SCI_TOKEN_RE,             m => { const k = token(); map[k] = m; return k; })
    // Keep common academic labels in English (Hinglish style)
    .replace(LOCKED_ENGLISH_RE,        m => { const k = token(); map[k] = m; return k; })
    // Named proper terms (multi-word first)
    .replace(NAMED_RE,                m => { const k = token(); map[k] = m; return k; })
    // All-caps abbreviations: AP, GP, HP, AM, GM, JEE, NEET, LHS, RHS …
    .replace(ABBREV_RE,               m => { const k = token(); map[k] = m; return k; });
  return { text: result, map };
}

function restoreProtected(text, map) {
  return Object.entries(map).reduce((t, [k, v]) => t.split(k).join(v), text);
}

// ─── Caching layer ─────────────────────────────────────────────────────────

const MEM_CACHE = new Map();            // in-memory (cleared on page reload)
const LS_PREFIX  = 'ppx_t_v11_';       // centralized glossary mode cache

function cacheGet(key) {
  if (MEM_CACHE.has(key)) return MEM_CACHE.get(key);
  try {
    const v = localStorage.getItem(LS_PREFIX + key);
    if (v) { MEM_CACHE.set(key, v); return v; }
  } catch (_) {}
  return null;
}

function cacheSet(key, value) {
  MEM_CACHE.set(key, value);
  try { localStorage.setItem(LS_PREFIX + key, value); } catch (_) {}
}

// ─── Concurrent queue ──────────────────────────────────────────────────────
// Kept for interface compatibility (progress + batching), even though
// translation is now deterministic and local (no network API calls).

let active = 0;
const taskQueue = [];
const MAX_CONCURRENT = 12;

function processQueue() {
  // Spin up as many tasks as allowed — no awaiting here, so the loop runs fully
  while (active < MAX_CONCURRENT && taskQueue.length > 0) {
    active++;
    const { fn, resolve, reject } = taskQueue.shift();
    fn()
      .then(resolve)
      .catch(reject)
      .finally(() => { active--; processQueue(); });
  }
}

function enqueue(fn) {
  return new Promise((resolve, reject) => {
    taskQueue.push({ fn, resolve, reject });
    processQueue();
  });
}

// ─── Verified dictionary translation (no external API) ────────────────────

const VERIFIED_EXACT_MAP = new Map([
  ['differentiation', 'अवकलन'],
  ['differentiation and applications', 'अवकलन और अनुप्रयोग'],
  ['integration', 'समाकलन'],
  ['definite integration & areas', 'निश्चित समाकलन एवं क्षेत्रफल'],
  ['sequences and series', 'अनुक्रम और श्रेणी'],
  ['quadratic equations', 'द्विघात समीकरण'],
  ['quadratic equations & inequalities', 'द्विघात समीकरण एवं असमानताएँ'],
  ['vectors & 3d geometry', 'सदिश एवं त्रिविम ज्यामिति'],
  ['vectors and 3d geometry', 'सदिश एवं त्रिविम ज्यामिति'],
  ['probability', 'प्रायिकता'],
  ['permutation and combination', 'क्रमचय और संचय'],
  ['limits, continuity and differentiability', 'सीमा, सततता तथा अवकलनीयता'],
  ['current electricity', 'धारा विद्युत'],
  ['electrostatics', 'स्थिर वैद्युतिकी'],
  ['magnetism and matter', 'चुंबकत्व एवं पदार्थ'],
  ['thermodynamics', 'ऊष्मागतिकी'],
  ['chemical bonding', 'रासायनिक बंधन'],
  ['atomic structure', 'परमाणु संरचना'],
  ['equilibrium', 'साम्यावस्था'],
  ['cell: structure and function', 'कोशिका: संरचना एवं कार्य'],
  ['genetics', 'आनुवंशिकी'],
]);

const VERIFIED_WORD_RULES = [
  [/\bdifferentiation\b/gi, 'अवकलन'],
  [/\bderivative(s)?\b/gi, 'अवकलज'],
  [/\bintegration\b/gi, 'समाकलन'],
  [/\bdefinite integral\b/gi, 'निश्चित समाकल'],
  [/\bindefinite integral\b/gi, 'अनिश्चित समाकल'],
  [/\blimits?\b/gi, 'सीमा'],
  [/\bcontinuity\b/gi, 'सततता'],
  [/\bdifferentiability\b/gi, 'अवकलनीयता'],
  [/\bsequence(s)?\b/gi, 'अनुक्रम'],
  [/\bseries\b/gi, 'श्रेणी'],
  [/\bpermutation\b/gi, 'क्रमचय'],
  [/\bcombination\b/gi, 'संचय'],
  [/\bvector(s)?\b/gi, 'सदिश'],
  [/\bmatrix\b/gi, 'आव्यूह'],
  [/\bdeterminant\b/gi, 'सारणिक'],
  [/\bprobability\b/gi, 'प्रायिकता'],
  [/\btrigonometry\b/gi, 'त्रिकोणमिति'],
  [/\bcoordinate geometry\b/gi, 'निर्देशांक ज्यामिति'],
  [/\bvelocity\b/gi, 'वेग'],
  [/\bacceleration\b/gi, 'त्वरण'],
  [/\bforce\b/gi, 'बल'],
  [/\bmomentum\b/gi, 'संवेग'],
  [/\benergy\b/gi, 'ऊर्जा'],
  [/\bpower\b/gi, 'शक्ति'],
  [/\bcurrent\b/gi, 'धारा'],
  [/\bresistance\b/gi, 'प्रतिरोध'],
  [/\bcapacitance\b/gi, 'धारिता'],
  [/\bmagnetic field\b/gi, 'चुंबकीय क्षेत्र'],
  [/\belectric field\b/gi, 'विद्युत क्षेत्र'],
  [/\bthermodynamics\b/gi, 'ऊष्मागतिकी'],
  [/\batom\b/gi, 'परमाणु'],
  [/\bmolecule\b/gi, 'अणु'],
  [/\belement\b/gi, 'तत्व'],
  [/\bcompound\b/gi, 'यौगिक'],
  [/\boxidation\b/gi, 'ऑक्सीकरण'],
  [/\breduction\b/gi, 'अपचयन'],
  [/\bequilibrium\b/gi, 'साम्यावस्था'],
  [/\bcell\b/gi, 'कोशिका'],
  [/\bgene\b/gi, 'जीन'],
  [/\bchromosome\b/gi, 'गुणसूत्र'],
  [/\bphotosynthesis\b/gi, 'प्रकाश संश्लेषण'],
  [/\brespiration\b/gi, 'श्वसन'],
  [/\btheorem\b/gi, 'प्रमेय'],
  [/\bdefinition\b/gi, 'परिभाषा'],
  [/\bexample\b/gi, 'उदाहरण'],
  [/\bsolution\b/gi, 'समाधान'],
  [/\bquestion\b/gi, 'प्रश्न'],
  [/\banswer\b/gi, 'उत्तर'],
];

function normalizeKey(text) {
  return String(text || '').toLowerCase().replace(/\s+/g, ' ').trim();
}

const NORMALIZED_VERIFIED_EXACT_OVERRIDES = new Map(
  Array.from(VERIFIED_EXACT_OVERRIDES.entries()).map(([k, v]) => [normalizeKey(k), v])
);

const NORMALIZED_VERIFIED_EXACT_MAP = new Map(
  Array.from(VERIFIED_EXACT_MAP.entries()).map(([k, v]) => [normalizeKey(k), v])
);

function applyVerifiedHindi(sourceText) {
  const src = String(sourceText || '');
  const key = normalizeKey(src);
  if (!key) return src;

  if (NORMALIZED_VERIFIED_EXACT_OVERRIDES.has(key)) return NORMALIZED_VERIFIED_EXACT_OVERRIDES.get(key);
  if (NORMALIZED_VERIFIED_EXACT_MAP.has(key)) return NORMALIZED_VERIFIED_EXACT_MAP.get(key);

  // Verified-only strategy: replace only known academic terms, keep rest as English.
  let out = src;
  for (const [re, replacement] of VERIFIED_WORD_RULES) {
    out = out.replace(re, replacement);
  }
  for (const [re, replacement] of VERIFIED_RULE_OVERRIDES) {
    out = out.replace(re, replacement);
  }

  // Correct known bad literal terms if present in stale content.
  out = out
    .replace(/भेदभाव/g, 'अवकलन')
    .replace(/वेक्टर/g, 'सदिश')
    .replace(/जाल/g, 'सामान्य त्रुटि');
  for (const [re, replacement] of VERIFIED_FIX_REPLACEMENTS) {
    out = out.replace(re, replacement);
  }

  return out;
}

// ─── Per-string translation (with cache + queue) ──────────────────────────

// Strings that must never be sent to the API
const SKIP_KEYS = new Set([
  'svg','latex','equation','file','thumb','src','href','path','slug','id','subject','type',
]);

function skipString(text) {
  if (!text || text.trim().length < 2) return true;
  if (/^\d+(\.\d+)?$/.test(text.trim())) return true;       // pure number
  if (/^[a-z0-9_-]+$/.test(text.trim())) return true;       // slug/id
  return false;
}

const HINDI_TERM_GLOSSARY = [
  // Core math terms
  { en: /\bdifferentiation\b/i, preferred: 'अवकलन', wrong: [/भेदभाव/g, /डिफरेंशिएशन/gi] },
  { en: /\bderivative(s)?\b/i, preferred: 'अवकलज', wrong: [/व्युत्पन्न/g] },
  { en: /\bintegration\b/i, preferred: 'समाकलन', wrong: [/एकीकरण/g] },
  { en: /\bdefinite integral\b/i, preferred: 'निश्चित समाकल', wrong: [] },
  { en: /\bindefinite integral\b/i, preferred: 'अनिश्चित समाकल', wrong: [] },
  { en: /\blimit(s)?\b/i, preferred: 'सीमा', wrong: [] },
  { en: /\bcontinuity\b/i, preferred: 'सातत्य', wrong: [] },
  { en: /\bfunction(s)?\b/i, preferred: 'फलन', wrong: [] },
  { en: /\binverse function\b/i, preferred: 'व्युत्क्रम फलन', wrong: [] },
  { en: /\bdomain\b/i, preferred: 'परिभाषा क्षेत्र', wrong: [] },
  { en: /\brange\b/i, preferred: 'मान क्षेत्र', wrong: [] },
  { en: /\bseries\b/i, preferred: 'श्रेणी', wrong: [] },
  { en: /\bsequence\b/i, preferred: 'अनुक्रम', wrong: [] },
  { en: /\bpermutation\b/i, preferred: 'क्रमचय', wrong: [] },
  { en: /\bcombination\b/i, preferred: 'संचय', wrong: [] },
  { en: /\bvector(s)?\b/i, preferred: 'सदिश', wrong: [/वेक्टर/g] },
  { en: /\bmatrix\b/i, preferred: 'आव्यूह', wrong: [/मैट्रिक्स/gi] },
  { en: /\bdeterminant\b/i, preferred: 'सारणिक', wrong: [] },
  { en: /\bprobability\b/i, preferred: 'प्रायिकता', wrong: [] },
  { en: /\btrigonometry\b/i, preferred: 'त्रिकोणमिति', wrong: [] },
  { en: /\bcoordinate geometry\b/i, preferred: 'निर्देशांक ज्यामिति', wrong: [] },
  { en: /\bcircle\b/i, preferred: 'वृत्त', wrong: [] },
  { en: /\bparabola\b/i, preferred: 'परवलय', wrong: [] },
  { en: /\bellipse\b/i, preferred: 'दीर्घवृत्त', wrong: [] },
  { en: /\bhyperbola\b/i, preferred: 'अतिपरवलय', wrong: [] },

  // Physics
  { en: /\bvelocity\b/i, preferred: 'वेग', wrong: [] },
  { en: /\bacceleration\b/i, preferred: 'त्वरण', wrong: [] },
  { en: /\bforce\b/i, preferred: 'बल', wrong: [] },
  { en: /\bmomentum\b/i, preferred: 'संवेग', wrong: [] },
  { en: /\bwork\b/i, preferred: 'कार्य', wrong: [] },
  { en: /\bpower\b/i, preferred: 'शक्ति', wrong: [] },
  { en: /\benergy\b/i, preferred: 'ऊर्जा', wrong: [] },
  { en: /\belectric field\b/i, preferred: 'विद्युत क्षेत्र', wrong: [] },
  { en: /\bpotential difference\b/i, preferred: 'विभवांतर', wrong: [] },
  { en: /\bcurrent\b/i, preferred: 'धारा', wrong: [] },
  { en: /\bresistance\b/i, preferred: 'प्रतिरोध', wrong: [] },
  { en: /\bcapacitance\b/i, preferred: 'धारिता', wrong: [] },
  { en: /\bmagnetic field\b/i, preferred: 'चुंबकीय क्षेत्र', wrong: [] },
  { en: /\binductance\b/i, preferred: 'प्रेरकत्व', wrong: [] },
  { en: /\bthermodynamics\b/i, preferred: 'ऊष्मागतिकी', wrong: [] },
  { en: /\bentropy\b/i, preferred: 'एंट्रॉपी', wrong: [] },

  // Chemistry
  { en: /\batom\b/i, preferred: 'परमाणु', wrong: [] },
  { en: /\bmolecule\b/i, preferred: 'अणु', wrong: [] },
  { en: /\belement\b/i, preferred: 'तत्व', wrong: [] },
  { en: /\bcompound\b/i, preferred: 'यौगिक', wrong: [] },
  { en: /\bvalency\b/i, preferred: 'संयोजकता', wrong: [] },
  { en: /\boxidation\b/i, preferred: 'ऑक्सीकरण', wrong: [] },
  { en: /\breduction\b/i, preferred: 'अपचयन', wrong: [] },
  { en: /\bequilibrium\b/i, preferred: 'साम्यावस्था', wrong: [] },
  { en: /\bionic\b/i, preferred: 'आयनिक', wrong: [] },
  { en: /\bcovalent\b/i, preferred: 'सहसंयोजक', wrong: [] },
  { en: /\bhybridization\b/i, preferred: 'संकरण', wrong: [] },
  { en: /\borbital\b/i, preferred: 'कक्षक', wrong: [] },

  // Biology
  { en: /\bcell\b/i, preferred: 'कोशिका', wrong: [] },
  { en: /\btissue\b/i, preferred: 'ऊतक', wrong: [] },
  { en: /\borgan\b/i, preferred: 'अंग', wrong: [] },
  { en: /\bgene\b/i, preferred: 'जीन', wrong: [] },
  { en: /\bchromosome\b/i, preferred: 'गुणसूत्र', wrong: [] },
  { en: /\bphotosynthesis\b/i, preferred: 'प्रकाश संश्लेषण', wrong: [] },
  { en: /\brespiration\b/i, preferred: 'श्वसन', wrong: [] },
  { en: /\benzyme\b/i, preferred: 'एंजाइम', wrong: [] },

  // Label quality fixes (literal machine Hindi -> classroom phrasing)
  { en: /\btrap\b/i, preferred: 'Common Trap', wrong: [/जाल/g, /फंदा/g] },
  { en: /\bshortcut\b/i, preferred: 'Shortcut', wrong: [/शॉर्टकट तरकीब/g, /शॉर्टकट युक्ति/g] },
  { en: /\btip\b/i, preferred: 'Tip', wrong: [/सुझाव/g] },
];

function applyHindiGlossary(sourceText, translatedText) {
  let out = translatedText;
  const src = sourceText || '';

  for (const item of HINDI_TERM_GLOSSARY) {
    const englishAppears = item.en.test(src);

    for (const wrongRe of item.wrong) {
      out = out.replace(wrongRe, item.preferred);
    }

    if (englishAppears) {
      // If API kept English term, convert it to preferred academic Hindi.
      const fallbackEnGlobal = new RegExp(item.en.source, 'gi');
      out = out.replace(fallbackEnGlobal, item.preferred);
    }
  }

  return out;
}

async function translateString(text, langCode) {
  const key = `${langCode}:${text.trim()}`;
  const cached = cacheGet(key);
  if (cached) return cached;

  // Fully disabled: no auto-translation.
  const result = text;
  cacheSet(key, result);
  return result;
}

// ─── Chapter-level cache ───────────────────────────────────────────────────

const CHAPTER_CACHE = new Map();

// ─── Public API: translate an entire chapter object ───────────────────────

// Count how many translatable strings are in a node (for progress reporting)
function countStrings(node) {
  if (node == null) return 0;
  if (typeof node === 'string') return skipString(node) ? 0 : 1;
  if (Array.isArray(node)) return node.reduce((s, i) => s + countStrings(i), 0);
  if (typeof node === 'object') {
    if ('english' in node || 'hindi' in node) return 0; // bilingual — no API needed
    return Object.entries(node).reduce((s, [k, v]) => s + (SKIP_KEYS.has(k) ? 0 : countStrings(v)), 0);
  }
  return 0;
}

export async function translateObjectFields(node, targetLang, tick) {
  // Manual bilingual mode only.
  // No generated translation; just resolve existing {english,hindi} values.
  return resolveText(node, targetLang);
}

/**
 * Translate a full chapter object with optional progress reporting.
 * Results are cached in memory + localStorage — instant on repeat visits.
 *
 * @param {object} chapter
 * @param {string} targetLang
 * @param {{ onStart?: (total:number)=>void, onTick?: (done:number, total:number)=>void }} [progress]
 */
export async function translateChapter(chapter, targetLang, progress = {}) {
  if (targetLang === 'english') return chapter;
  const cacheKey = `${chapter.slug}__${targetLang}`;
  if (CHAPTER_CACHE.has(cacheKey)) return CHAPTER_CACHE.get(cacheKey);

  // Check localStorage for a previously translated full chapter
  try {
    const stored = localStorage.getItem(`ppx_ch_v11_${cacheKey}`);
    if (stored) {
      const parsed = JSON.parse(stored);
      CHAPTER_CACHE.set(cacheKey, parsed);
      return parsed;
    }
  } catch (_) {}

  // Manual bilingual mode: instant toggle using hardcoded values.
  if (progress.onStart) progress.onStart(0);
  const translated = resolveText(chapter, targetLang);
  if (progress.onTick) progress.onTick(0, 0);
  CHAPTER_CACHE.set(cacheKey, translated);
  try { localStorage.setItem(`ppx_ch_v11_${cacheKey}`, JSON.stringify(translated)); } catch (_) {}
  return translated;
}

function hashString(value) {
  let h = 5381;
  for (let i = 0; i < value.length; i++) {
    h = ((h << 5) + h) + value.charCodeAt(i);
    h |= 0;
  }
  return String(h >>> 0);
}

const HTML_SKIP_TAGS = new Set([
  'script', 'style', 'noscript', 'code', 'pre', 'svg', 'math', 'iframe',
]);

function hasClassLike(el, classRegex) {
  let cursor = el;
  while (cursor && cursor.nodeType === 1) {
    if (cursor.className && classRegex.test(String(cursor.className))) return true;
    cursor = cursor.parentElement;
  }
  return false;
}

function shouldSkipHtmlTextNode(node) {
  const raw = node?.nodeValue || '';
  if (!raw.trim()) return true;

  const parentTag = node.parentNode?.nodeName?.toLowerCase() || '';
  if (HTML_SKIP_TAGS.has(parentTag)) return true;

  const parentEl = node.parentElement;
  // Skip known formula containers from cheatsheet templates.
  if (parentEl && hasClassLike(parentEl, /(formula|equation|katex|math|matrix|expr|latex)/i)) {
    return true;
  }

  // If text contains math delimiters or latex commands, do not translate.
  if (/[\\][a-zA-Z]+/.test(raw)) return true;
  if (/[{}^_=]/.test(raw)) return true;
  if (raw.includes('$')) return true;

  // Skip symbol-heavy strings (typically expressions, determinants, vector notation).
  const symbolCount = (raw.match(/[\\{}^_=<>|]/g) || []).length;
  if (symbolCount >= 2) return true;

  return skipString(raw);
}

/**
 * Translate HTML content (handbooks/cheatsheets) with the same engine used by notes.
 * Uses queue + cache and reports progress for UI overlays.
 */
export async function translateHtmlString(html, targetLang = 'hindi', progress = {}, cacheHint = '') {
  // Fully disabled in manual bilingual mode.
  if (progress.onStart) progress.onStart(0);
  if (progress.onTick) progress.onTick(0, 0);
  return html;
}
